import {
  LoanChecklist,
  LoanChecklistRuleResultResponse,
  LoanChecklistRuleResults,
} from './types';
import { isMockServices } from 'src/utils';
import mock from 'src/services/utils/mock';
import { QueryFunction } from '@tanstack/react-query';
import { createId } from '@paralleldrive/cuid2';
import { getCompatibleAuthTokenHttp } from '../utils/enhancedAuthTokenHttp';

export interface GetChecklistRulesParams {
  loanId: string;
  loanChecklistData: LoanChecklist[];
  useFilter: boolean;
}

const getLoanChecklistRules: QueryFunction<
  LoanChecklistRuleResults[],
  ['/loans/checklist-rules', GetChecklistRulesParams]
> = async ({ queryKey }) => {
  if (isMockServices()) return await mock(_mockData);

  const authTokenHttp = await getCompatibleAuthTokenHttp();
  if (!queryKey[1].loanId) {
    throw new Error('Loan ID is required to fetch checklist rules');
  }
  const response = await authTokenHttp<LoanChecklistRuleResultResponse>(
    `/${queryKey[1].loanId}/loan-checklist-rule-results`,
    {
      method: 'GET',
    },
  );

  if (response.data) {
    if (response.data.loanChecklistRuleResults === undefined) {
      return [];
    }

    if (queryKey[1].useFilter) {
      return filterRuleResultsByChecklist(
        response.data.loanChecklistRuleResults,
        queryKey[1].loanChecklistData,
      );
    }

    return response.data.loanChecklistRuleResults;
  }

  throw new Error('Failed to fetch loan checklist rules');
};

export default getLoanChecklistRules;

const filterRuleResultsByChecklist = (
  ruleResults: LoanChecklistRuleResults[],
  checklist: LoanChecklist[],
) => {
  const output = ruleResults
    .map((result) => {
      const checklistItem = checklist.find(
        (item) => item.id === result.loanChecklistRuleId,
      );

      if (!checklistItem) return null;

      const filteredRules = result.rules.filter((rule) =>
        checklistItem.rules.some((cRule) => cRule.name === rule.name),
      );

      return filteredRules.length > 0
        ? {
            ...result,
            rules: filteredRules.map((rule) => {
              return {
                ...rule,
                id: createId(),
              };
            }),
          }
        : null;
    })
    .filter((r): r is LoanChecklistRuleResults => r !== null);
  return output;
};

const _mockData: LoanChecklistRuleResults[] = new Array(20)
  .fill(null)
  .map((_, index) => ({
    id: createId(),
    loanChecklistRuleId: `${index + 1}`,
    rules: new Array(12).fill(null).map((_, ruleIndex) => ({
      id: `${ruleIndex + 1}`,
      name: 'Mock Rule Name ' + (ruleIndex + 1),
      result: 'Pass',
      trueConstraint: `True Message ${ruleIndex + 1}`,
      falseConstraint: `Fail Message ${ruleIndex + 1}`,
      errorMessage: ``,
      evaluatedExpression: '5000 == 5000',
      userTaskId: '',
    })),
  }));
